import { set } from "date-fns/set";
import isNumber from "lodash/isNumber";
import { IntlShape } from "react-intl";
import {
  AutoCorrectDurationConfig,
  AutoCorrectInput,
  BookingScheduleTime,
} from "@/lib/types/schedule-types";
import { formatIntl } from "@/lib/utils/calendar-utils/calendar-utils";

const TimeData = [
  { time_index: 0, time: "6:00 am", h: 6, m: 0, overnight: false },
  { time_index: 1, time: "6:30 am", h: 6, m: 30, overnight: false },
  { time_index: 2, time: "7:00 am", h: 7, m: 0, overnight: false },
  { time_index: 3, time: "7:30 am", h: 7, m: 30, overnight: false },
  { time_index: 4, time: "8:00 am", h: 8, m: 0, overnight: false },
  { time_index: 5, time: "8:30 am", h: 8, m: 30, overnight: false },
  { time_index: 6, time: "9:00 am", h: 9, m: 0, overnight: false },
  { time_index: 7, time: "9:30 am", h: 9, m: 30, overnight: false },
  { time_index: 8, time: "10:00 am", h: 10, m: 0, overnight: false },
  { time_index: 9, time: "10:30 am", h: 10, m: 30, overnight: false },
  { time_index: 10, time: "11:00 am", h: 11, m: 0, overnight: false },
  { time_index: 11, time: "11:30 am", h: 11, m: 30, overnight: false },
  { time_index: 12, time: "12:00 pm", h: 12, m: 0, overnight: false },
  { time_index: 13, time: "12:30 pm", h: 12, m: 30, overnight: false },
  { time_index: 14, time: "1:00 pm", h: 13, m: 0, overnight: false },
  { time_index: 15, time: "1:30 pm", h: 13, m: 30, overnight: false },
  { time_index: 16, time: "2:00 pm", h: 14, m: 0, overnight: false },
  { time_index: 17, time: "2:30 pm", h: 14, m: 30, overnight: false },
  { time_index: 18, time: "3:00 pm", h: 15, m: 0, overnight: false },
  { time_index: 19, time: "3:30 pm", h: 15, m: 30, overnight: false },
  { time_index: 20, time: "4:00 pm", h: 16, m: 0, overnight: false },
  { time_index: 21, time: "4:30 pm", h: 16, m: 30, overnight: false },
  { time_index: 22, time: "5:00 pm", h: 17, m: 0, overnight: false },
  { time_index: 23, time: "5:30 pm", h: 17, m: 30, overnight: false },
  { time_index: 24, time: "6:00 pm", h: 18, m: 0, overnight: false },
  { time_index: 25, time: "6:30 pm", h: 18, m: 30, overnight: false },
  { time_index: 26, time: "7:00 pm", h: 19, m: 0, overnight: false },
  { time_index: 27, time: "7:30 pm", h: 19, m: 30, overnight: false },
  { time_index: 28, time: "8:00 pm", h: 20, m: 0, overnight: false },
  { time_index: 29, time: "8:30 pm", h: 20, m: 30, overnight: false },
  { time_index: 30, time: "9:00 pm", h: 21, m: 0, overnight: false },
  { time_index: 31, time: "9:30 pm", h: 21, m: 30, overnight: false },
  { time_index: 32, time: "10:00 pm", h: 22, m: 0, overnight: false },
  { time_index: 33, time: "10:30 pm", h: 22, m: 30, overnight: false },
  { time_index: 34, time: "11:00 pm", h: 23, m: 0, overnight: false },
  { time_index: 35, time: "11:30 pm", h: 23, m: 30, overnight: false },
  { time_index: 36, time: "12:00 am", h: 0, m: 0, overnight: true },
  { time_index: 37, time: "12:30 am", h: 0, m: 30, overnight: true },
  { time_index: 38, time: "1:00 am", h: 1, m: 0, overnight: true },
  { time_index: 39, time: "1:30 am", h: 1, m: 30, overnight: true },
  { time_index: 40, time: "2:00 am", h: 2, m: 0, overnight: true },
  { time_index: 41, time: "2:30 am", h: 2, m: 30, overnight: true },
  { time_index: 42, time: "3:00 am", h: 3, m: 0, overnight: true },
  { time_index: 43, time: "3:30 am", h: 3, m: 30, overnight: true },
  { time_index: 44, time: "4:00 am", h: 4, m: 0, overnight: true },
  { time_index: 45, time: "4:30 am", h: 4, m: 30, overnight: true },
  { time_index: 46, time: "5:00 am", h: 5, m: 0, overnight: true },
  { time_index: 47, time: "5:30 am", h: 5, m: 30, overnight: true },
  { time_index: 48, time: "6:00 am", h: 6, m: 0, overnight: true },
] as BookingScheduleTime[];

/**
 * Normalize time data to ensure all required fields are present
 * @param timeData - Partial<BookingScheduleTime>[]
 * @returns BookingScheduleTime[]
 */
const normalizeTimeData = (
  timeData: Partial<BookingScheduleTime>[],
): BookingScheduleTime[] => {
  return timeData
    .map((time) => {
      const timeObj = TimeData.find((t) => t.time_index === time.time_index);
      if (!timeObj) {
        return false;
      }
      return { ...timeObj, ...time };
    })
    .filter(Boolean) as BookingScheduleTime[];
};

/**
 * Generate time options for start and end time selection based on a start time
 * @param startTime - The selected start time
 * @param intl - The intl object
 * @returns TimeOptions object with filtered start and end time options
 */
const generateTimeOptions = (
  startTime: BookingScheduleTime | null,
  intl: IntlShape,
): { start: BookingScheduleTime[]; end: BookingScheduleTime[] } => {
  const startTimeIndex = startTime?.time_index;
  const formatTime = formatIntl(intl);
  const timeOptions = TimeData.map((time) => {
    const date = set(new Date(), { hours: time.h, minutes: time.m });
    return {
      ...time,
      time: formatTime(date, "p"),
    };
  });

  return {
    start: timeOptions.filter((time) => !time.overnight),
    end:
      typeof startTimeIndex === "number"
        ? timeOptions.filter(
            (time) =>
              time.time_index !== undefined && time.time_index > startTimeIndex,
          )
        : timeOptions,
  };
};

const detectUSTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const usTimezones = new Set([
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "America/Anchorage",
      "Pacific/Honolulu",
      "America/Phoenix",
      "America/Detroit",
      "America/Louisville",
      "America/Indiana/Indianapolis",
    ]);

    const isUSTimezone = usTimezones.has(timezone);

    return isUSTimezone;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Could not detect user location:", error);
    return false;
  }
};

const autoCorrectEndTime = ({
  config,
  value,
  timeOptions,
}: {
  config: AutoCorrectDurationConfig;
  value: {
    startTime: BookingScheduleTime | undefined;
    endTime: BookingScheduleTime | undefined;
  };
  timeOptions: BookingScheduleTime[];
}): AutoCorrectInput["value"] => {
  const { exactDuration, minDuration, onAutoCorrect } = config;
  const startTimeIndex = value?.startTime?.time_index || 0;
  let correctEndTime = value?.endTime;

  if (exactDuration) {
    const correctEndTimeIndex = startTimeIndex + exactDuration * 2;
    if (correctEndTimeIndex !== value?.endTime?.time_index) {
      onAutoCorrect?.("exactDuration");
      correctEndTime = timeOptions.find(
        (option) => option.time_index === correctEndTimeIndex,
      );
    }
  } else if (minDuration) {
    const correctEndTimeIndex = startTimeIndex + minDuration * 2;
    if (
      !isNumber(value?.endTime?.time_index) ||
      correctEndTimeIndex > value.endTime.time_index
    ) {
      onAutoCorrect?.("minDuration");
      correctEndTime = timeOptions.find(
        (option) => option.time_index === correctEndTimeIndex,
      );
    }
  }

  return {
    ...value,
    endTime: normalizeTimeData([correctEndTime || {}])[0],
  };
};

/**
 * Auto-corrects the duration of a time selection
 * @param inputToCorrect - "startTime" or "endTime"
 * @param config - AutoCorrectDurationConfig
 * @param timeOptions - TimeOptions
 * @param value - The time select value to auto-correct
 * @returns A new time select value with the correct duration
 */
const autoCorrectDuration = ({
  inputToCorrect = "endTime",
  config,
  timeOptions,
  value,
}: AutoCorrectInput): AutoCorrectInput["value"] => {
  const { exactDuration, minDuration } = config;
  if (!exactDuration && !minDuration) {
    return value;
  }

  if (inputToCorrect === "endTime") {
    return autoCorrectEndTime({
      config,
      value,
      timeOptions: timeOptions.end || [],
    });
  }

  return value;
};

export {
  autoCorrectDuration,
  generateTimeOptions,
  normalizeTimeData,
  TimeData,
  detectUSTimezone,
};
